import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const S = ({ num, title, children }) => (
  <section data-testid={`privacy-section-${num}`} className="mt-12">
    <h2 className="font-display text-2xl sm:text-3xl font-medium text-kiki-ink">{num}. {title}</h2>
    <div className="mt-4 space-y-3 text-base text-kiki-muted font-light leading-relaxed">{children}</div>
  </section>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="bg-[#FAF5EC] min-h-screen text-kiki-ink warm-spotlight">
      <header className="border-b border-kiki-line bg-[#FAF5EC]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center">
          <Link data-testid="privacy-back-link" to="/" className="flex items-center gap-2.5 text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit">
            <ArrowLeft className="w-4 h-4" />
            <img src="/logo.png" alt="Kiki Services" className="w-8 h-8 rounded-full object-cover border border-kiki-line" />
            <span className="font-display text-xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal><Eyebrow>Legal</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-kiki-ink mt-3 leading-[1.05]">
            Privacy <span className="italic text-kiki-gold">Policy</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
            <p className="font-mono text-xs uppercase tracking-widest text-kiki-gold">Effective Date: 9 September 2026</p>
            <p>
              Kiki Services (“Kiki Services”, “we”, “us” or “our”) respects your privacy and is committed to
              handling personal information responsibly.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, store and protect information when you visit or
              interact with Kikiservices.co.in (the “Website”), contact us, submit an enquiry, or otherwise
              communicate with Kiki Services through channels connected with our services.
            </p>
            <p>By using the Website, you acknowledge that you have read and understood this Privacy Policy.</p>
          </div>
        </Reveal>

        <S num="1" title="About Kiki Services">
          <p>Kiki Services is a consulting-led practice founded by Bhakti Narang.</p>
          <p>
            We provide consulting and business support services that may include HR and people systems,
            recruitment and process consulting, business process improvement, business analysis, data-related
            support, brand and digital coordination, content and digital support, and related professional services.
          </p>
        </S>

        <S num="2" title="Information We May Collect">
          <p>Depending on how you interact with us, we may collect information such as:</p>
          <p className="font-outfit font-medium text-kiki-ink">Information you provide directly</p>
          <p>This may include:</p>
          <Ul items={[
            "Name",
            "Email address",
            "Phone number",
            "Company or organisation name",
            "Job title or professional information",
            "Information included in an enquiry or message",
            "Information you voluntarily provide when requesting our services",
            "Information required to communicate with you regarding an enquiry, project or business relationship",
          ]} />
          <p>Please avoid submitting unnecessary sensitive or confidential personal information through general website forms.</p>
          <p className="font-outfit font-medium text-kiki-ink">Information collected automatically</p>
          <p>
            When you visit our Website, certain technical information may be collected automatically by the
            website infrastructure, hosting provider, security systems or analytics tools we may use. This may include:
          </p>
          <Ul items={[
            "IP address",
            "Browser and device information",
            "Operating system",
            "Pages visited",
            "Approximate usage information",
            "Date and time of access",
            "Referring website or source",
          ]} />
          <p>The exact information collected may depend on the technical services and integrations active on the Website.</p>
        </S>

        <S num="3" title="How We Use Your Information">
          <p>We may use personal information for legitimate business purposes, including to:</p>
          <Ul items={[
            "Respond to enquiries and requests",
            "Communicate with prospective or existing clients",
            "Understand your requirements",
            "Provide consulting or related services",
            "Prepare proposals, estimates or other business communications",
            "Manage client relationships and projects",
            "Improve our Website, services and user experience",
            "Maintain Website security and prevent misuse",
            "Maintain business records",
            "Comply with applicable legal and regulatory obligations",
            "Communicate important service-related information",
          ]} />
          <p>We will seek to use personal information only for purposes that are relevant to the interaction or service involved.</p>
        </S>

        <S num="4" title="Consent and Lawful Processing">
          <p>Where consent is required under applicable law, we will request consent before processing personal information for the relevant purpose.</p>
          <p>
            Where permitted by applicable law, information may also be processed where necessary for legitimate
            or lawful business purposes, including providing requested services, fulfilling contractual
            obligations, complying with legal requirements, or protecting the Website and our rights.
          </p>
          <p>
            Where processing is based on consent, you may withdraw that consent, subject to applicable legal
            requirements and any consequences that may reasonably follow from such withdrawal.
          </p>
        </S>

        <S num="5" title="Contact and Enquiry Forms">
          <p>
            If you submit an enquiry through the Website, the information you provide may be used to respond to
            your request and, where relevant, discuss potential services or a business relationship.
          </p>
          <p>Please provide only information that is reasonably necessary for your enquiry.</p>
          <p>
            We may retain enquiry information for an appropriate period for business administration, follow-up,
            record keeping, legal compliance or legitimate business purposes.
          </p>
        </S>

        <S num="6" title="Sharing of Information">
          <p>Kiki Services does not intend to sell your personal information.</p>
          <p>We may share information where reasonably necessary with:</p>
          <Ul items={[
            "Service providers who help operate our Website or business",
            "Hosting, website, communication, analytics or technology providers",
            "Professional advisers where necessary",
            "Payment or transaction service providers, where applicable",
            "Government authorities, regulators, courts or law-enforcement agencies where required by law",
            "Other parties where disclosure is necessary to protect our legal rights, safety, security or business interests",
          ]} />
          <p>Where third-party service providers process personal information on our behalf, we seek to use appropriate providers and reasonable safeguards.</p>
        </S>

        <S num="7" title="Third-Party Services and Links">
          <p>The Website may use or link to third-party websites, platforms, applications or services.</p>
          <p>
            These may include communication platforms, social media platforms, payment services, analytics tools,
            forms, hosting services or other technology providers.
          </p>
          <p>When you interact with a third-party service, that service may process information according to its own privacy policy and terms.</p>
          <p>Kiki Services is not responsible for the privacy practices of third-party websites or platforms that we do not control.</p>
          <p>We encourage you to review the privacy policies of third-party services before providing information to them.</p>
        </S>

        <S num="8" title="Cookies and Similar Technologies">
          <p>
            The Website may use cookies or similar technologies that are necessary for website functionality,
            security, performance, analytics or other legitimate purposes.
          </p>
          <p>Where required by applicable law, we will provide appropriate information or obtain consent for non-essential cookies or similar technologies.</p>
          <p>You may be able to control cookies through your browser settings. Disabling certain cookies may affect the functionality of parts of the Website.</p>
        </S>

        <S num="9" title="Data Security">
          <p>
            We take reasonable technical and organisational measures to protect personal information against
            unauthorised access, misuse, alteration, disclosure, loss or destruction.
          </p>
          <p>However, no method of transmitting or storing information electronically can be guaranteed to be completely secure.</p>
          <p>You should therefore also take reasonable precautions when communicating or sharing information online.</p>
        </S>

        <S num="10" title="Data Retention">
          <p>
            We retain personal information only for as long as reasonably necessary for the purpose for which it
            was collected, for providing services, maintaining appropriate business records, complying with legal
            obligations, resolving disputes, enforcing agreements, or protecting our legitimate interests.
          </p>
          <p>The actual retention period may vary depending on the type of information and the purpose for which it is processed.</p>
          <p>
            When information is no longer required, we may securely delete, anonymise or otherwise dispose of it,
            subject to applicable legal or operational requirements.
          </p>
        </S>

        <S num="11" title="Your Privacy Rights">
          <p>Subject to applicable law, you may have rights relating to your personal information, including the ability to:</p>
          <Ul items={[
            "Request information about how your personal data is being processed",
            "Request correction of inaccurate or incomplete personal information",
            "Request deletion or erasure where applicable",
            "Withdraw consent where processing is based on consent",
            "Raise a complaint regarding the handling of your personal information",
            "Exercise other rights available to you under applicable data-protection law",
          ]} />
          <p>Requests may be made using the contact details provided below.</p>
          <p>We may need to verify your identity before acting on certain requests in order to protect your information from unauthorised access.</p>
        </S>

        <S num="12" title="Children’s Privacy">
          <p>The Website and Kiki Services' professional services are intended primarily for businesses, professionals and general users.</p>
          <p>We do not knowingly seek to collect personal information from children for purposes unrelated to our services.</p>
          <p>
            If you believe that a child has provided personal information to us without appropriate authorisation,
            please contact us so that we can review the matter and take appropriate action.
          </p>
        </S>

        <S num="13" title="Confidential Business Information">
          <p>Kiki Services may receive confidential business information from prospective or existing clients during consulting engagements.</p>
          <p>Such information may be subject to additional contractual confidentiality obligations, agreements or project-specific arrangements.</p>
          <p>
            This Privacy Policy does not replace any separate confidentiality agreement, service agreement,
            non-disclosure agreement or other contractual arrangement that may apply to a particular engagement.
          </p>
        </S>

        <S num="14" title="Changes to This Privacy Policy">
          <p>We may update this Privacy Policy from time to time to reflect changes in:</p>
          <Ul items={[
            "Our services",
            "Website functionality",
            "Technology or third-party integrations",
            "Applicable laws or regulations",
            "Our data-handling practices",
          ]} />
          <p>The updated version will be published on this page with a revised effective date.</p>
          <p>We encourage you to review this page periodically.</p>
        </S>

        <S num="15" title="Contact Us">
          <p>
            If you have questions, requests or concerns regarding this Privacy Policy or the handling of your
            personal information, please contact Kiki Services through the contact details provided on the Website.
          </p>
          <div className="rounded-2xl border border-kiki-line bg-white p-6 space-y-2">
            <p className="font-display text-xl text-kiki-ink">Kiki Services</p>
            <p>Founded by Bhakti Narang</p>
            <p>Website: Kikiservices.co.in</p>
            <p>Privacy enquiries: <a href="mailto:bhaktinarang@kikiservices.co.in" className="text-kiki-gold underline">bhaktinarang@kikiservices.co.in</a></p>
            <p>Phone / WhatsApp: +91 98679 82112</p>
            <p>Business/contact address: India (remote-first consulting practice)</p>
          </div>
        </S>

        <S num="16" title="Grievance / Privacy Requests">
          <p>For privacy-related requests or complaints, please contact us using the official contact details published on the Website.</p>
          <p>
            When submitting a request, please clearly state the nature of your request and provide sufficient
            information for us to identify the relevant interaction or information.
          </p>
          <p>We will review and respond to requests in accordance with applicable law and the circumstances of the request.</p>
        </S>

        <p className="mt-16 pt-8 border-t border-kiki-line font-mono text-[10px] uppercase tracking-widest text-kiki-muted">
          Last updated: 9 September 2026
        </p>
      </div>
    </div>
  );
};

export default PrivacyPage;
